
import {Component, Prop} from "vue-property-decorator";
import {Getter} from "vuex-class";
import OverPriceSms from "@/components/OverPriceSms.vue";
import {formRules, regex} from "@/utils/formRules";
import { mixins } from "vue-class-component";
import DocumentMixin from "@/mixins/DocumentMixin.vue";
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";
import KeyAddGuestDialog from "@/components/keys/guests/KeyAddGuestDialog.vue";

const REFRESH_TIME_MILLI: number = 2000;

@Component({
    components: {
        KeyAddGuestDialog,
        MylobyAutocomplete,
        OverPriceSms,
    },
})
export default class HandToHand extends mixins(DocumentMixin) {

    get displayQrCode(): boolean {
        return this.handToHandQrCode && !this.verificationBySMS && !this.verificationByEmail;
    }

    get displayValidatePinByEmail(): boolean {
        return this.handToHandSelectedGuest?.id && this.handToHandQrCode && this.verificationByEmail;
    }

    get displayValidatePinBySms(): boolean {
        return this.handToHandSelectedGuest?.id && this.handToHandQrCode && this.verificationBySMS;
    }

    get canEditGuest(): boolean {
        return this.canDoInEnvCompany("MANAGE_KEY_GUESTS");
    }

    get isPhoneValid(): boolean {
        return regex.validPhoneNumber.test(this.handToHandSelectedGuest?.phone);
    }

    get isEmailValid(): boolean {
        return formRules.isEmail(this.handToHandSelectedGuest?.email);
    }

    @Prop({default: {}})
    public currentKey: any;

    @Prop({default: false})
    public disabled: boolean;

    public $refs!: {
        handToHandSelectGuestAutocomplete: any,
    };

    @Getter private currentUser: any;
  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private isMobile: boolean;

    private acceptedOverPrice: boolean = undefined;
    private handToHandDialog: boolean = false;
    private handToHandSelectedGuest: any = null;
    private autoSelectedKeyGuest: number = null;
    private handToHandQrCode: string = null;
    private loadHandToHand: boolean = false;
    private verificationBySMS: boolean = false;
    private verificationByEmail: boolean = false;
    private codePin: string = null;
    private loadingPinValidation: boolean = false;
    private loadingSms: boolean = false;
    private pinFailed: boolean = false;
    private addGuestModal: boolean = false;
    private acceptOverPrice: boolean = false;
    private rules: object = formRules;
    private msgSent: boolean = false;
    private modalType: string = null;
    private intervalId: any = null;

    private openAddGuest(): void {
        this.addGuestModal = true;
    }

  private addedGuest(keyAccess: any): void {

    try {
      this.$store.dispatch("userGroups/getUsersInGroup", {
        id: keyAccess.userGroupId,
      }).then((rows) => {
        const user = rows.users?.[0];
        const group = user?.UsersGroup?.[0];
        if (group && group.type === "user") {
          // Here, we want to auto-select the user newly added (only if it is a solo user)
          this.autoSelectedKeyGuest = this.handToHandSelectedGuest = user;
        } else if (this.$refs.handToHandSelectGuestAutocomplete) {
          this.$refs.handToHandSelectGuestAutocomplete.getFirstItems();
        }
        this.addGuestModal = false;
      });
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error);
    }
    // If a group was added, the user needs to select manually which user he wants to give the key to
    this.addGuestModal = false;
  }

    private selectGuestHandToHand() {
        this.loadHandToHand = true;
        if (!this.verificationBySMS) {
            return this.$store.dispatch("keys/giveHandToHand", {
                keyId: this.currentKey.id,
                sourceUserId: this.currentUser.id,
                targetUserId: this.handToHandSelectedGuest.id,
            }).then((res: any) => {
              if (res.status === "OK") {
                  // Key has been given directly
                  this.successCallback();
              } else {
                  // Waiting for target user confirmation
                  this.handToHandQrCode = res.qrCode;
                  this.checkGuestHandToHand();
                  this.intervalId = setInterval(() => this.checkGuestHandToHand(), REFRESH_TIME_MILLI);
              }
            }).catch(() => {
                this.$store.commit("alerts/displayError", {
                    iconGroup: "icon-picto_donner-main-a-main",
                    msg: this.$i18n?.t("alerts.error.default"),
                });
                this.$emit("reset");
                this.closeDialog();
            }).finally(() => {
                this.loadHandToHand = false;
            });
        }
    }

    private checkGuestHandToHand(): Promise<void> {
        return this.$store.dispatch("keys/checkHandToHand", {
                keyId: this.currentKey.id,
                targetUserId: this.handToHandSelectedGuest.id,
            }).then((data) => {
                if (data === "OK") {
                    this.successCallback();
                }
            }).catch((err) => {
                this.$store.commit("alerts/displayError", {
                    msg: err,
                });
            });
    }

    private cancelGuestHandToHand() {
        this.$store.dispatch("keys/cancelHandToHand", {
            keyId: this.currentKey.id,
        });
        this.handToHandQrCode = null;
        this.handToHandSelectedGuest = null;
    }

    private switchSmsOrQR() {
        this.pinFailed = false;
        this.verificationBySMS = !this.verificationBySMS;
        if (this.verificationBySMS) {
            this.sendSMS(false);
        }
    }

    private backToQR(): void {
        this.pinFailed = false;
        this.verificationBySMS = false;
        this.verificationByEmail = false;
    }

    private switchMailOrQR(): void {
        this.pinFailed = false;
        this.verificationByEmail = !this.verificationByEmail;
        if (this.verificationByEmail) {
            this.sendMail();
        }
    }

    private sendSMS(acceptedOverPrice): Promise<void> {
        if (acceptedOverPrice) {
            this.acceptedOverPrice = true;
        }
        this.loadingSms = true;
        this.modalType = null;
        return this.$store.dispatch("keys/sendSms", {
            keyId: this.currentKey.id,
            sourceUserId: this.currentUser.id,
            targetUserId: this.handToHandSelectedGuest.id,
            acceptOverPrice: this.acceptOverPrice,
            fromModal: true,
        }).then((res) => {
            this.msgSent = res.msg === "mailSent";
            if (this.msgSent) {
                this.$store.commit("alerts/displaySuccess", {
                    msg: this.$i18n?.t("pin.pinViaMail", {mail: res.mail}),
                });
            }
            this.acceptOverPrice = false;
        }).catch((error) => {
            switch (error?.response?.data?.message) {
                case "smsLimitReached":
                    this.acceptOverPrice = true;
                    this.modalType = "smsLimitReached";
                    break;
                case "smsOverpriceLimitReached":
                    this.acceptOverPrice = true;
                    this.modalType = "smsOverpriceLimitReached";
                    break;
                default:
                    this.$store.commit("alerts/displayHttpError", { error });
                    this.cancelHandToHand();
                    break;
            }
        }).finally(() => {
            this.loadingSms = false;
        });
    }

    private sendMail(): Promise<void> {
        return this.$store.dispatch("keys/sendMail", {
            keyId: this.currentKey.id,
            sourceUserId: this.currentUser.id,
            targetUserId: this.handToHandSelectedGuest.id,
        }).then((res) => {
            this.msgSent = res.msg === "mailSent";
            if (this.msgSent) {
                this.$store.commit("alerts/displaySuccess", {
                    msg: this.$i18n?.t("pin.pinViaMail", {mail: res.mail}),
                });
            }
        });
    }

    private validatePin(notificationType: string): Promise<void> {
        this.pinFailed = false;
        if (!this.codePin) {
            return;
        }
        this.loadingPinValidation = true;
        return this.$store.dispatch("keys/pinVerification", {
            keyId: this.currentKey.id,
            pin: this.codePin,
            targetUserId: this.handToHandSelectedGuest.id,
            acceptOverPrice: this.acceptedOverPrice,
            notificationType,
        }).then(() => {
            this.successCallback();
        }).catch((error) => {
            switch (error?.response?.data?.message) {
                case "handToHandNotFound":
                    this.pinFailed = true;
                    break;
                default:
                    this.$store.commit("alerts/displayHttpError", { error });
                    this.handToHandDialog = false;
                    this.verificationBySMS = false;
                    break;
            }
        }).finally(() => {
            this.loadingPinValidation = false;
        });
    }

    private successCallback() {
        this.$store.dispatch("context/clearContext");
        this.notifyUser();
        this.$emit("reset");
        this.closeDialog();
    }

    private notifyUser(): void {
        this.$store.commit("alerts/displaySuccess", {
            msg: this.$i18n?.t("alerts.key.giveSuccess", {
                key: this.currentKey?.name,
                user: this.handToHandSelectedGuest?.displayName,
            }),
        });
    }

    private cancelHandToHand() {
      this.closeDialog();
      this.cancelGuestHandToHand();
    }

    private closeDialog() {
        clearInterval(this.intervalId);
        this.acceptOverPrice = false;
        this.handToHandDialog = false;
        this.autoSelectedKeyGuest = null;
        this.handToHandSelectedGuest = null;
        this.verificationBySMS = false;
        this.verificationByEmail = false;
    }

    private beforeDestroy(): void {
        clearInterval(this.intervalId);
    }

    private canDropOrGive = (user, key) => key?.status === "CREATED"
        || key?.status === "UNARCHIVED"
        || (key?.status !== "IN_RELAY" && this.userHasKey(user, key))

    private userHasKey = (user, key) => key?.KeyHolding?.find((kH) => !kH.endDate)?.userId === user?.id;
}
